import { useRef, useReducer, useCallback } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DOMPurify from 'dompurify'
import { identity } from 'lodash'
import { selectIsUserAdmin } from '@authed/selectors'
import { useAppSelector } from '@redux/hooks'
import { DecoratedActions } from './components/decoratedActions'
import { InvalidFileName } from './components/invalidFileName'
import { Phases } from './phases'
import { ActionType, initialState, PHASES, reducer } from './reducer'
import * as css from './styles'
import { isFileOfType, selectFile } from '../../global/utils/fileUtil'
import { ApprovalRequiredText } from '../dataset/approvalRequiredText'
import PropTypes from 'prop-types'
import { t } from '@lingui/core/macro'

/**
 * Santize the filename string.
 * @param {string} filename The filename to import.
 * @returns {string}
 */
function sanitizeFilename(filename) {
  const sanitized = DOMPurify.sanitize(filename, { USE_PROFILES: { html: false } })
  return sanitized
}

const initialFormData = new FormData()

export const FileUploadModal = ({
  SuccessPhase = null,
  expectedExtension = '.csv',
  expectedMimeTypes = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  decorateActions = identity,
  onUpload = () => new Promise(resolve => resolve(null)),
  open,
  renderErrors = JSON.stringify,
  setOpen,
  title = t`Upload CSV File`,
  requireApproval = false,
}) => {
  const formDataRef = useRef(initialFormData)
  const formData = formDataRef.current
  const isAdmin = useAppSelector(selectIsUserAdmin)
  const [state, dispatch] = useReducer(reducer, initialState)
  const { fileName, currentPhase, errors, invalidFileName } = state

  function handleClose() {
    clearFormData()
    dispatch({ type: 'reset' })
    setOpen(false)
  }

  /**
   * Handle setting the state of the filename and if it's valid.
   * - Show error message if invalid. e.g. Once sanitized, invalid shows as extension.
   * @param {string} filename The filename to be imported.
   */
  const setSanitizedFileName = useCallback(
    function setSanitizedFileName(filename) {
      const sanitized = sanitizeFilename(filename)
      if (sanitized === '.csv') {
        dispatch({ type: 'setInvalidName', payload: true })
        clearFormData()
        return // Important: do not set filename if invalid.
      }

      dispatch({ type: 'setFilename', payload: sanitized })
    },
    [dispatch],
  )

  const displayFileSelector = useCallback(
    function displayFileSelector() {
      return selectFile(expectedMimeTypes).then(file => {
        formDataRef.current = new FormData()
        const sanitized = sanitizeFilename(file.name)
        formDataRef.current?.append('selectedFile', file, sanitized)
        setSanitizedFileName(sanitized)
        return formDataRef.current
      })
    },
    [expectedMimeTypes, setSanitizedFileName],
  )

  function handleDrop(e) {
    e.preventDefault()
    const fileToUpload = e.dataTransfer.files[0]
    if (!isFileOfType(fileToUpload, expectedMimeTypes, expectedExtension)) return
    const sanitized = sanitizeFilename(fileToUpload.name)

    formData.append('selectedFile', fileToUpload, sanitized)
    setSanitizedFileName(sanitized)
  }

  function onClickUpload() {
    dispatch({ type: ActionType.setCurrentPhase, payload: PHASES.UPLOADING })
    onUpload(formData)
      .then(() => {
        dispatch({ type: ActionType.setCurrentPhase, payload: PHASES.SUCCESS })
      })
      .catch(error => {
        // IF there was an error but there's no error data
        // THEN return an array with an empty object to coerce the default error message
        clearFormData()
        dispatch({ type: ActionType.setFilename, payload: '' })
        dispatch({ type: ActionType.setErrors, payload: error.response?.data?.errors || [] })
      })
  }

  function clearFormData() {
    formDataRef.current = new FormData()
  }

  function onDeleteSelectedFile() {
    formDataRef.current = new FormData()
    dispatch({ type: 'reset' })
  }

  const decoratedActions = decorateActions(
    <DecoratedActions
      fileName={fileName}
      currentPhase={currentPhase}
      handleClose={handleClose}
      onClickUpload={onClickUpload}
    />,
    { phase: currentPhase, handleClose, onClickUpload },
  )

  return (
    <Dialog
      aria-describedby="alert-dialog-description"
      aria-labelledby="alert-dialog-title"
      onClose={handleClose}
      data-testid="bulk-upload-modal"
      open={open}
    >
      <DialogTitle
        style={{ backgroundColor: 'rgb(235, 237, 238)', borderBottom: '1px solid #ccc' }}
      >
        {title}
      </DialogTitle>
      {invalidFileName ? <InvalidFileName /> : null}
      <Phases
        current={currentPhase}
        {...{
          errors,
          expectedMimeTypes,
          fileName,
          handleClose,
          onDeleteSelectedFile,
          handleDrop,
          onSelectFileToUpload: displayFileSelector,
          renderErrors,
        }}
      >
        {SuccessPhase ? <SuccessPhase /> : null}
      </Phases>
      <css.DialogActionsContainer requireApproval={requireApproval}>
        <css.DialogActionsStyled>{decoratedActions}</css.DialogActionsStyled>
        {requireApproval && (
          <css.ApprovalTextContainer>
            <ApprovalRequiredText isAdmin={isAdmin} />
          </css.ApprovalTextContainer>
        )}
      </css.DialogActionsContainer>
    </Dialog>
  )
}

FileUploadModal.propTypes = {
  SuccessPhase: PropTypes.node,
  expectedExtension: PropTypes.string,
  expectedMimeTypes: PropTypes.array,
  decorateActions: PropTypes.func,
  onUpload: PropTypes.func,
  open: PropTypes.bool,
  renderErrors: PropTypes.func,
  setOpen: PropTypes.func,
  title: PropTypes.string,
  requireApproval: PropTypes.bool,
}

FileUploadModal.PHASES = PHASES
